import React from 'react';
import { CircleCheckBig as TickIcon } from 'lucide-react';
import { assetSrc } from '../../../utils/metadata';

// Decorative gradient corner bracket framing the QR preview (1.0's
// icons.qrcodehash).
const QR_DASH = assetSrc('icons/qrdash.png');

interface SuccessViewProps {
  t: (key: string, defaultValue?: string) => string;
  selectedModel: string;
  variantName: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  selectedPrefix: string;
  /** Object URL for the QR PNG returned by configuration/v2 (GM only). */
  qrCodeUrl?: string;
  onDownloadQR?: () => void;
  onOpenQR?: () => void;
  onClose: () => void;
}

const SuccessView: React.FC<SuccessViewProps> = ({
  t,
  selectedModel,
  variantName,
  firstName,
  lastName,
  email,
  phone,
  selectedPrefix,
  qrCodeUrl,
  onDownloadQR,
  onOpenQR,
  onClose,
}) => {
  return (
    <div className="save-dialog__content-wrapper save-dialog__content-wrapper--success">
      {/* Scrolls on its own so the QR box can never push the footer CTA out of
          the dialog (or sit on top of it). */}
      <div className="save-dialog__success-body">
        <div className="save-dialog__success-header">
          <div className="save-dialog__success-icon">
            <TickIcon color="#00B654" />
          </div>
          <h2 className="save-dialog__success-title">
            {t(
              'staticTranslations.saveDialog.successTitle',
              'Enquiry Submitted'
            )}
          </h2>
        </div>

        <div className="save-dialog__details-grid">
          {(selectedModel || 'Traverse') && (
            <div className="save-dialog__detail-item">
              <span className="save-dialog__detail-label">
                {t(
                  'staticTranslations.saveDialog.successLabelModel',
                  'Model name'
                )}{' '}
                :
              </span>
              <span className="save-dialog__detail-value">
                {selectedModel || 'Traverse'}
              </span>
            </div>
          )}
          {variantName && (
            <div className="save-dialog__detail-item">
              <span className="save-dialog__detail-label">
                {t(
                  'staticTranslations.saveDialog.successLabelVariant',
                  'Variant name'
                )}{' '}
                :
              </span>
              <span className="save-dialog__detail-value">{variantName}</span>
            </div>
          )}
          {(firstName || lastName) && (
            <div className="save-dialog__detail-item">
              <span className="save-dialog__detail-label">
                {t('staticTranslations.saveDialog.successLabelName', 'Name')} :
              </span>
              <span className="save-dialog__detail-value">
                {firstName} {lastName}
              </span>
            </div>
          )}
          {email && (
            <div className="save-dialog__detail-item">
              <span className="save-dialog__detail-label">
                {t('staticTranslations.saveDialog.successLabelEmail', 'Email')}{' '}
                :
              </span>
              <span className="save-dialog__detail-value">{email}</span>
            </div>
          )}
          {phone && (
            <div className="save-dialog__detail-item">
              <span className="save-dialog__detail-label">
                {t(
                  'staticTranslations.saveDialog.successLabelMobile',
                  'Mobile'
                )}{' '}
                :
              </span>
              <span className="save-dialog__detail-value">
                {selectedPrefix} {phone}
              </span>
            </div>
          )}
        </div>

        {qrCodeUrl && (
          <div className="save-dialog__qr-section">
            <div className="save-dialog__qr-row">
              <div className="save-dialog__qr-code-wrapper">
                <img src={QR_DASH} alt="" className="save-dialog__qr-dash-1" />
                <div className="save-dialog__qr-preview">
                  <img
                    src={qrCodeUrl}
                    alt="QR Code"
                    className="save-dialog__qrcode"
                  />
                </div>
                <img src={QR_DASH} alt="" className="save-dialog__qr-dash-2" />
              </div>

              <div className="save-dialog__qr-info">
                <h5 className="save-dialog__qr-title">
                  {t(
                    'staticTranslations.saveDialog.qrTitle',
                    'Your Unique Configuration QR'
                  )}
                </h5>
                <div>
                  <p className="save-dialog__qr-desc">
                    {t(
                      'staticTranslations.saveDialog.qrDescription',
                      'Show this QR code to your nearest dealer to access your saved configuration.'
                    )}
                  </p>
                  <p className="save-dialog__qr-desc save-dialog__qr-note">
                    <strong>
                      {t('staticTranslations.saveDialog.qrNoteLabel', 'Note:')}
                    </strong>{' '}
                    {t(
                      'staticTranslations.saveDialog.qrNote',
                      'Only authorized dealers can view this configuration.'
                    )}
                  </p>
                </div>

                <div className="save-dialog__qr-actions">
                  <button
                    type="button"
                    className="save-dialog__qr-action-btn save-dialog__qr-download"
                    onClick={onDownloadQR}
                  >
                    {t(
                      'staticTranslations.saveDialog.qrDownload',
                      'Download QR'
                    )}
                  </button>
                  {/* Hidden in 1.0 too (`.saveQr { visibility: hidden }`);
                      kept so the pair can be re-enabled from CSS alone. */}
                  <button
                    type="button"
                    className="save-dialog__qr-action-btn save-dialog__qr-save"
                    onClick={onOpenQR}
                  >
                    {t('staticTranslations.saveDialog.qrSave', 'Save QR')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <button className="save-dialog__submit-button" onClick={onClose}>
        {t('staticTranslations.saveDialog.buttonClose', 'Okay, Got it')}
      </button>
    </div>
  );
};

export default SuccessView;
